import React, { forwardRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TimeFields from './TimeFields';
import './DatePicker.css';

// Calendar icon component
const CalendarIcon = ({ size = 14 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Custom input component with calendar icon
const CustomInput = forwardRef(({ value, onClick, onChange, placeholder, onClear, hasValue }, ref) => (
  <div className="date-picker__input-wrapper">
    <input
      ref={ref}
      type="text"
      className="date-picker__input"
      value={value}
      onClick={onClick}
      onChange={onChange}
      placeholder={placeholder || 'DD/MM/YYYY'}
    />
    <span className="date-picker__icon" onClick={onClick}>
      <CalendarIcon size={14} />
    </span>
    {hasValue && (
      <button 
        type="button" 
        className="date-picker__clear" 
        onClick={(e) => {
          e.stopPropagation();
          onClear?.();
        }}
        title="Clear date"
      >
        ×
      </button>
    )}
  </div>
));

CustomInput.displayName = 'CustomInput';

/**
 * DatePicker Component
 *
 * Calendar for visual selection. Inline panels use DateTimePopover for the title.
 */
const DatePicker = ({
  selected,
  onChange,
  onClear,
  placeholder = 'DD/MM/YYYY',
  dateFormat = 'dd/MM/yyyy',
  showTimeSelect = false,
  timeFormat = 'HH:mm',
  portalId = 'date-picker-portal',
  popperPlacement = 'bottom-start',
  inline = false,
  ...props
}) => {
  // Parse date if it's a string
  const selectedDate = selected ? (typeof selected === 'string' ? new Date(selected) : selected) : null;
  
  if (inline) {
    return (
      <div className="date-picker__inline-container">
        <ReactDatePicker
          selected={selectedDate}
          onChange={onChange}
          inline
          showTimeSelect={showTimeSelect}
          showTimeInput={showTimeSelect}
          customTimeInput={showTimeSelect ? <TimeFields /> : undefined}
          timeFormat={timeFormat}
          calendarClassName="date-picker__inline-calendar"
          {...props}
        />
      </div>
    );
  }
  
  return (
    <ReactDatePicker
      selected={selectedDate}
      onChange={onChange}
      customInput={
        <CustomInput 
          hasValue={!!selectedDate} 
          onClear={onClear}
          placeholder={placeholder}
        />
      }
      dateFormat={showTimeSelect ? `${dateFormat} ${timeFormat}` : dateFormat}
      showTimeSelect={showTimeSelect}
      showTimeInput={showTimeSelect}
      customTimeInput={showTimeSelect ? <TimeFields /> : undefined}
      timeFormat={timeFormat}
      portalId={portalId}
      popperPlacement={popperPlacement}
      popperModifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
          },
        },
      ]}
      showPopperArrow={false}
      {...props}
    />
  );
};

export default DatePicker;
